import React, { useMemo, useState } from "react";
import { Spinner, Button, Form, Table, Placeholder } from "react-bootstrap";
import { useVehicleAvailabilityCalendar } from "../../../../../api/fleet-management/dashboard/dashboard.hooks";

const toISODate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

// Return a range starting from the provided date and ending one month later
const getOneMonthRangeFrom = (date = new Date()) => {
  const start = new Date(date);
  const end = new Date(date);
  end.setMonth(end.getMonth() + 1);
  return [toISODate(start), toISODate(end)];
};

const getDatesBetween = (startISO, endISO) => {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const dates = [];

  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    dates.push(toISODate(current));
  }

  return dates;
};

const safe = (value) => (value === null || value === undefined ? "-" : value);
const getTodayISO = () => toISODate(new Date());

const VehicleAvailabilityCalendar = ({ startDate: propsStartDate, endDate: propsEndDate }) => {
  const [fallbackStart, fallbackEnd] = getOneMonthRangeFrom();
  const initialStart = toISODate(propsStartDate) || fallbackStart;
  const initialEnd = toISODate(propsEndDate) || fallbackEnd;

  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);

  const params = useMemo(() => ({ startDate: start, endDate: end }), [start, end]);
  const { data, isLoading, isFetching, isError, error, refetch } = useVehicleAvailabilityCalendar(params);

  const { vehicles, dates } = useMemo(() => {
    const fallbackDates = getDatesBetween(start, end);
    const payload = data?.data || data;
    const vehiclesMap = new Map();

    if (payload && Array.isArray(payload.vehicles)) {
      payload.vehicles.forEach((vehicle) => {
        const vehicleId = vehicle.vehicleId || vehicle.vehicleId?._id || vehicle._id || vehicle.id;
        const label = vehicle.vehicleName || vehicle.vehicleSnapshot?.name || vehicle.snapshot?.name || `${vehicleId}`;
        const availability = new Map();

        (vehicle.availability || []).forEach((cell) => {
          const day = toISODate(cell.date || cell.day || cell);
          if (!day) return;
          availability.set(day, {
            totalStock: vehicle.totalStock ?? vehicle.stock ?? null,
            booked: cell.booked ?? null,
            available: cell.available ?? null,
          });
        });

        vehiclesMap.set(String(vehicleId), {
          label,
          availability,
          totalStock: vehicle.totalStock ?? null,
        });
      });
    }

    const normalizedVehicles = Array.from(vehiclesMap.entries()).map(([vehicleId, value]) => ({
      vehicleId,
      ...value,
    }));

    return {
      vehicles: normalizedVehicles,
      dates: payload?.dates || fallbackDates,
    };
  }, [data, start, end]);

  const todayISO = useMemo(() => getTodayISO(), []);
  const isBusy = isLoading || isFetching;
  const showSkeleton = isLoading && vehicles.length === 0;
  const rowCount = showSkeleton ? 3 : vehicles.length;

  const formatShortDate = (iso) => {
    try {
      const date = new Date(iso);
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit" }).format(date);
    } catch (err) {
      return iso;
    }
  };

  if (isError) {
    return (
      <div className="p-3 card text-danger">
        <strong>Error loading availability</strong>
        <div className="small mt-1">{String(error?.message || error)}</div>
      </div>
    );
  }

  if (!showSkeleton && vehicles.length === 0) {
    return (
      <div className="p-3 card">
        <div className="text-muted">No vehicle availability data found for the selected range.</div>
      </div>
    );
  }

  const renderCell = (day, vehicleIndex, rowIndex) => {
    const isToday = day === todayISO;
    const todayBackground = isToday ? { backgroundColor: "#eef8f2" } : undefined;

    if (showSkeleton) {
      return (
        <td
          key={`${vehicleIndex}-${rowIndex}-${day}`}
          style={{ minWidth: 108, width: 108, textAlign: "center", verticalAlign: "middle", ...todayBackground }}
        >
        </td>
      );
    }

    const vehicle = vehicles[vehicleIndex];
    const cell = vehicle.availability.get(day) || {};

    return (
      <td
        key={`${vehicle.vehicleId}-${day}`}
        style={{ minWidth: 108, width: 108, textAlign: "center", verticalAlign: "middle", ...todayBackground }}
      >
        <div className="small text-muted">{cell.totalStock != null ? cell.totalStock : "-"}</div>
        <div className="small text-danger lh-sm">Booked: {safe(cell.booked)}</div>
        <div className="small text-success lh-sm">Available: {safe(cell.available)}</div>
      </td>
    );
  };

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="fw-bold fs-5">Vehicle Availability</div>

        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          <Form.Label className="small text-muted mb-0 me-2">Range</Form.Label>
          <Form.Control type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ width: 160 }} />
          <span className="mx-2">→</span>
          <Form.Control type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ width: 160 }} />
          {/* <Button variant="outline-primary" size="sm" className="ms-2" onClick={() => refetch()} disabled={isBusy}>
            {isBusy ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Refreshing...
              </>
            ) : (
              "Refresh"
            )}
          </Button> */}
        </div>
      </div>

        <div className="table-responsive border rounded">
          <Table bordered hover className="mb-0 align-middle" style={{ minWidth: Math.max(900, 260 + dates.length * 108) }}>
          <thead>
            <tr>
              <th className="position-sticky start-0 top-0 bg-body-tertiary" style={{ minWidth: 260, width: 260, left: 0, zIndex: 4 }}>
                Vehicle / Type
              </th>
              {dates.map((day) => (
                <th
                  key={day}
                  style={{
                    minWidth: 108,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: day === todayISO ? "#eaf5ee" : undefined,
                  }}
                >
                  {formatShortDate(day)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: rowCount }).map((_, rowIndex) => {
              const vehicle = vehicles[rowIndex];
              const rowKey = vehicle?.vehicleId || `skeleton-${rowIndex}`;

              return (
                <tr key={rowKey}>
                  <td className="position-sticky start-0 bg-white" style={{ minWidth: 260, width: 260, left: 0, zIndex: 3 }}>
                    {showSkeleton ? (
                      <>
                        <Placeholder as="div" animation="glow" className="mb-2">
                          <Placeholder xs={8} />
                        </Placeholder>
                        <Placeholder as="div" animation="glow">
                          <Placeholder xs={4} />
                        </Placeholder>
                      </>
                    ) : (
                      <>
                        <div className="fw-semibold">{vehicle.label}</div>
                        {vehicle.totalStock != null && <div className="small text-muted">Stock: {vehicle.totalStock}</div>}
                      </>
                    )}
                  </td>

                  {dates.map((day) => renderCell(day, rowIndex, rowIndex))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default VehicleAvailabilityCalendar;